/**
 * Client-Side Zero-Knowledge Crypto Service
 * 
 * Private keys NEVER leave the browser.
 * Uses X25519 (via tweetnacl) + XSalsa20-Poly1305 for E2E encryption.
 * Keys stored in IndexedDB, inaccessible to the server.
 */
import nacl from 'tweetnacl';
import { encodeBase64, decodeBase64, encodeUTF8, decodeUTF8 } from 'tweetnacl-util';

const DB_NAME = 'sg_crypto';
const STORE_NAME = 'keys';
const DB_VERSION = 1;

// ─── IndexedDB helpers ───

function openDB() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => req.result.createObjectStore(STORE_NAME);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function dbGet(key) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readonly');
    const req = tx.objectStore(STORE_NAME).get(key);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function dbPut(key, value) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    tx.objectStore(STORE_NAME).put(value, key);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

async function dbDelete(key) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    tx.objectStore(STORE_NAME).delete(key);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

// ─── Key Management ───

/**
 * Generate a new X25519 keypair and store private key in IndexedDB.
 * Returns the public key (base64) to send to the server.
 */
export async function generateAndStoreKeyPair(userId) {
  const keyPair = nacl.box.keyPair();
  // Store private key locally — NEVER sent to server
  await dbPut(`pk_${userId}`, encodeBase64(keyPair.secretKey));
  // Return public key for server storage
  return encodeBase64(keyPair.publicKey);
}

/**
 * Get the private key from IndexedDB for the given user.
 */
export async function getPrivateKey(userId) {
  const b64 = await dbGet(`pk_${userId}`);
  if (!b64) return null;
  return decodeBase64(b64);
}

/**
 * Check if we have a local private key for this user.
 */
export async function hasLocalKey(userId) {
  const b64 = await dbGet(`pk_${userId}`);
  return !!b64;
}

/**
 * Delete local keys (for logout / account deletion).
 */
export async function deleteLocalKeys(userId) {
  await dbDelete(`pk_${userId}`);
}

// ─── Encryption (client-side) ───

/**
 * Encrypt a message for a recipient using their public key.
 * Uses ephemeral X25519 keypair for Perfect Forward Secrecy.
 * Returns a JSON string (base64-encoded) to store on the server.
 * The server CANNOT decrypt this — only the recipient's browser can.
 */
export function encryptMessage(plaintext, recipientPublicKeyB64) {
  const recipientPub = decodeBase64(recipientPublicKeyB64);
  // Ephemeral keypair for PFS — destroyed after this function
  const ephemeral = nacl.box.keyPair();
  const nonce = nacl.randomBytes(nacl.box.nonceLength);
  const messageBytes = decodeUTF8(plaintext);
  const encrypted = nacl.box(messageBytes, nonce, recipientPub, ephemeral.secretKey);
  if (!encrypted) throw new Error('Encryption failed');

  const payload = {
    v: 3,  // Version 3 = client-side zero-knowledge
    eph: encodeBase64(ephemeral.publicKey),
    nonce: encodeBase64(nonce),
    ct: encodeBase64(encrypted),
    e2e_encrypted: true,
  };
  return btoa(JSON.stringify(payload));
}

// ─── Decryption (client-side) ───

/**
 * Decrypt a message using the local private key.
 * Supports v3 (client-side NaCl) and falls back for legacy v1/v2 (server-encrypted).
 */
export function decryptMessage(encryptedPayloadB64, privateKey) {
  try {
    const json = atob(encryptedPayloadB64);
    const payload = JSON.parse(json);

    if (!payload.e2e_encrypted) return null;
    if (payload.v !== 3) return null;  // Can't decrypt v1/v2 (server-side keys)

    const ephPub = decodeBase64(payload.eph);
    const nonce = decodeBase64(payload.nonce);
    const ciphertext = decodeBase64(payload.ct);
    const decrypted = nacl.box.open(ciphertext, nonce, ephPub, privateKey);
    if (!decrypted) return null;
    return encodeUTF8(decrypted);
  } catch {
    return null;
  }
}

/**
 * Check if a message was encrypted client-side (v3) vs server-side (v1/v2).
 */
export function isClientEncrypted(e2eContent) {
  if (!e2eContent) return false;
  try {
    const json = atob(e2eContent);
    const payload = JSON.parse(json);
    return payload.v === 3;
  } catch {
    return false;
  }
}
